import React, { Fragment, useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { notBlank } from "../../../util/ext";
import { useMediaTable } from "../../common/use-mediaTable";
import { useDeleteModuleItems } from "../moduleServices";
import { useRemoveContentFromCollection } from "../../collections/collectionServices";
import { faCircleMinus } from "@fortawesome/pro-solid-svg-icons";
import {
  applySpec,
  filter,
  findIndex,
  flatten,
  groupBy,
  map,
  nth,
  path,
  pipe,
  pluck,
  prop,
  propEq,
  values,
} from "ramda";

import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import CustomTablePagination from "../../common/CustomTablePagination";
import FontAwesomeIcon from "../../../components/FontAwesomeIcon";
import ModuleFilesTable from "./ModuleFilesTable";
import MediaBrowser from "../../common/MediaBrowser";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import RealTimeSearchBar from "../../common/RealTimeSearchBar";
import SelectedModuleFilesCheckbox from "./SelectedModuleFilesCheckbox";
import { useSelected } from "../../organize/OrganizeProvider";
import { isCollection, isModule } from "../../organize/organizeUtil";

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: "white",
    // for loading indicator
    position: "relative",
  },

  header: {
    padding: theme.spacing(5),
  },

  loadingIndicator: {
    paddingTop: theme.spacing(5),
  },

  moduleContainer: {
    padding: theme.spacing(3),
  },

  moduleDetails: {
    paddingLeft: theme.spacing(3),
  },

  search: {
    marginTop: theme.spacing(2),
  },

  thumbnailEmpty: {
    fontSize: "75px",
    color: theme.palette.primary.disabled,
  },

  thumbnailOverlay: {
    height: "100px",
    width: "100px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "20px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
    backgroundColor: "#E5E5E5",
  },

  title: {
    fontWeight: 700,
    color: theme.palette.darker,
    fontSize: "1.6rem",
    lineHeight: "24px",
  },
}));

const ModuleFilesBrowser = props => {
  const classes = useStyles();

  const {
    appliedQuery,
    content,
    filterMenu,
    handleDeleteItems,
    handlePageChange,
    handleQueryChange,
    handleSortChange,
    loading,
    module,
    onRowsPerPageChange,
    orderBy,
    order,
    page,
    queryPills,
    refetch,
    rowsPerPage,
    searchState,
    setLoading,
    SnackAlertHandler,
    sourceFilter,
    title = "Files",
    total,
  } = props;
  const { SnackAlert, showAlert } = SnackAlertHandler;
  const { selectedContent, removeAllSelectedContent } = useSelected();

  const [scrollVisible, setScrollVisible] = useState(true);
  const [contentList, setContentList] = useState([]);

  const { handleUpdateRowsPerPage } = useMediaTable(
    { onRowsPerPageChange },
    { rowsPerPage },
  );

  useEffect(() => {
    setContentList(content);
  }, [content]);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  const deleteModuleItems = useDeleteModuleItems();
  const removeContentFromCollection = useRemoveContentFromCollection();
  const handleRemoveContentFromModule = useCallback(async () => {
    try {
      setLoading(true);
      // if module is not found on parentModules,
      // then check parentCollections for all collections that might be included in module
      const directParents = map(content => {
        if (findIndex(propEq("id", module.id), content.parentModules) === -1) {
          const collectionParents = filter(collection => {
            if (
              findIndex(
                mod => mod.id === module.id,
                collection.ancestorModules,
              ) !== -1
            ) {
              return collection;
            }
          }, content.parentCollections);
          return map(
            cp => ({
              content,
              parent: cp,
            }),
            collectionParents,
          );
        } else {
          return {
            content,
            parent: module,
          };
        }
      }, selectedContent);

      const groupedByParent = pipe(
        flatten,
        groupBy(path(["parent", "id"])),
        values,
        map(
          applySpec({
            parent: pipe(nth(0), prop("parent")),
            contentIds: pipe(pluck("content"), pluck("id")),
          }),
        ),
      )(directParents);

      // Don't refetch query until all mutations end
      await Promise.all(
        map(({ contentIds, parent }) => {
          if (isCollection(parent)) {
            return removeContentFromCollection.mutateAsync({
              collectionId: parent.id,
              contentIds: contentIds,
              stopInvalidation: true,
            });
          }

          if (isModule(parent)) {
            return deleteModuleItems.mutateAsync({
              id: parent.id,
              ids: contentIds,
              stopInvalidation: true,
            });
          }
        }, groupedByParent),
      );

      refetch();
      removeAllSelectedContent();
    } catch (err) {
      showAlert("error", [err.message]);
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [
    deleteModuleItems,
    removeContentFromCollection,
    module,
    refetch,
    removeAllSelectedContent,
    selectedContent,
    setLoading,
    showAlert,
  ]);

  return (
    <Fragment>
      <Grid container item xs className={classes.root}>
        {loading && (
          <PanelLoadingIndicator
            transparent
            className={classes.loadingIndicator}
          />
        )}

        <SnackAlert />

        <Grid item xs={12}>
          <MediaBrowser
            loaded={true}
            organizeTable
            maxWidth={false}
            scrollVisible={scrollVisible}
            searchBar={
              <Grid container justify="space-between">
                <Grid container item xs spacing={3} alignItems="center">
                  <Fragment>
                    <Grid item>
                      <Typography className={classes.title}>
                        {title} ({total})
                      </Typography>
                    </Grid>

                    {notBlank(selectedContent) && (
                      <Grid item container xs alignItems="center">
                        <Grid item>
                          <SelectedModuleFilesCheckbox
                            selectedContent={selectedContent}
                            removeAllSelectedContent={removeAllSelectedContent}
                            total={total}
                            content={content}
                          />
                        </Grid>

                        <Grid item>
                          <Tooltip
                            title={`Remove ${selectedContent.length} items from module`}
                          >
                            <IconButton onClick={handleRemoveContentFromModule}>
                              <FontAwesomeIcon
                                variant="delete"
                                icon={faCircleMinus}
                              />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    )}
                  </Fragment>
                </Grid>
                <Grid item container justify="flex-end" alignItems="center" xs>
                  <Grid item xs={6}>
                    <RealTimeSearchBar
                      searchBarEnd
                      query={appliedQuery}
                      onQueryChange={handleQueryChange}
                    />
                  </Grid>

                  {notBlank(filterMenu) && (
                    <Grid item xs="auto">
                      {filterMenu}
                    </Grid>
                  )}
                </Grid>

                {notBlank(queryPills) && (
                  <Grid item container alignItems="center">
                    {queryPills}
                  </Grid>
                )}
              </Grid>
            }
            mediaTable={
              <ModuleFilesTable
                isLoading={loading}
                searchState={searchState}
                sourceFilter={sourceFilter}
                module={module}
                files={notBlank(contentList) ? contentList : []}
                setLoading={setLoading}
                onItemsDelete={handleDeleteItems}
                order={order}
                orderBy={orderBy}
                onSortChange={handleSortChange}
                onScrollVisible={handleScrollVisible}
              />
            }
          />
        </Grid>
      </Grid>

      <CustomTablePagination
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangeRowsPerPage={event =>
          handleUpdateRowsPerPage(+event.target.value)
        }
        onChangePage={handlePageChange}
      />
    </Fragment>
  );
};

export default ModuleFilesBrowser;
